import { useEffect, useId, type RefObject } from 'react';

/** Fired on `window` when a chrome menu or sheet opens; `detail` is its id. */
const OPENED = 'mg:shell-menu-opened';

/** The widths where the navigation changes shape (Tailwind's `md` and `lg`). */
const BREAKPOINTS = ['(min-width: 768px)', '(min-width: 1024px)'];

/**
 * The rules every menu and sheet that opens from the chrome shares (the workspace picker, the account
 * menu, New Content in each place, the More sheet). Each keeps its own `open` state; this:
 *  - closes it when another one opens, so at most one is open at a time;
 *  - closes it when the window is resized across `md` or `lg`, where the nav it hangs from changes
 *    shape (the page stays as it is);
 *  - on opening, moves focus to its first item; Escape closes it and puts focus back on its trigger.
 *
 * `root` holds both the trigger (the element with `aria-haspopup`) and the open panel.
 */
export function useShellMenu(open: boolean, close: () => void, root: RefObject<HTMLElement>): void {
  const id = useId();

  useEffect(() => {
    if (!open) return;
    window.dispatchEvent(new CustomEvent(OPENED, { detail: id }));

    const onOther = (e: Event) => {
      if ((e as CustomEvent<string>).detail !== id) close();
    };
    window.addEventListener(OPENED, onOther);

    const queries =
      typeof window.matchMedia === 'function' ? BREAKPOINTS.map((q) => window.matchMedia(q)) : [];
    queries.forEach((q) => q.addEventListener('change', close));

    // Capture phase: New Content stops Escape there (it must not reach the composer), and a listener
    // on the same node still runs.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const trigger = root.current?.querySelector<HTMLElement>('[aria-haspopup]');
      close();
      trigger?.focus();
    };
    document.addEventListener('keydown', onKey, true);

    const first = root.current?.querySelector<HTMLElement>(
      '[role="menu"] [role^="menuitem"]:not(:disabled), [role="dialog"] a[href], [role="dialog"] button:not(:disabled)',
    );
    first?.focus();

    return () => {
      window.removeEventListener(OPENED, onOther);
      queries.forEach((q) => q.removeEventListener('change', close));
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, close, root, id]);
}
